import { APP_ID, APP_INITIALIZER, ApplicationConfig, CSP_NONCE, ErrorHandler, importProvidersFrom, inject, Injector, Optional, TransferState } from '@angular/core';
import { provideLuxonDateAdapter } from '@angular/material-luxon-adapter';
import { MAT_DATE_LOCALE } from '@angular/material/core';
import { bootstrapApplication, provideClientHydration, withEventReplay, withNoHttpTransferCache } from '@angular/platform-browser';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { provideRouter, UrlSerializer, withEnabledBlockingInitialNavigation } from '@angular/router';
import { TranslateLoader, TranslateModule } from '@ngx-translate/core';
import { GlobalModule } from '@techmore/global';
import { UPLOAD_ASSETS } from '@techmore/upload';
import { Response } from 'express';
import { HttpClient, provideHttpClient, withFetch, withInterceptorsFromDi } from '@angular/common/http';
import { NgxPageScrollModule } from 'ngx-page-scroll';
import { NgxPageScrollCoreModule } from 'ngx-page-scroll-core';
import { environment } from 'src/environments/environment';
import { routes } from './app.routes';
import { initializationFactory } from './initializationFactory.fn';
import { HtmlSuffixURLSerializer } from './share/entity/html-suffix-url-serializer';
import { GlobalErrorHandlerService } from './share/service/global-error-handler.service';
import { translateBrowserLoaderFactory } from './share/service/translate-browser-loader.service';
import { CSP_NONCE_KEY } from './share/tokens/csp-nonce.statekey';
import { GENERATOR_ENV_TOKEN, GeneratorEnv } from './share/tokens/generator-env.token';
import { IS_BROWSER_TOKEN } from './share/tokens/is-browser.token';
import { REQUEST_MARK_KEY } from './share/tokens/request-mark.statekey';
import { REQUEST_MARK_TOKEN } from './share/tokens/request-mark.token';
import { IS_INTERNAL_KEY } from './share/tokens/is-internal.statekey';
import { INTERNAL_KEY, RESPONSE } from '../express.tokens';

const DATE_FORMATS = {
  parse: {
    dateInput: 'yyyy/MM/dd',
  },
  display: {
    dateInput: 'yyyy/MM/dd',
    monthYearLabel: 'yyyy MMMM',
    dateA11yLabel: 'DDD',
    monthYearA11yLabel: 'yyyy MMMM',
  },
};

export const appConfig: ApplicationConfig = {
  providers: [
    importProvidersFrom(
      TranslateModule.forRoot({
        loader: {
          provide: TranslateLoader,
          useFactory: translateBrowserLoaderFactory,
          deps: [HttpClient, TransferState],
        },
        defaultLanguage: 'zh-tw',
      }),
      NgxPageScrollCoreModule,
      NgxPageScrollModule,
      GlobalModule.forRoot(),
    ),
    { provide: APP_ID, useValue: 'serverApp' },
    { provide: MAT_DATE_LOCALE, useValue: 'zh-TW' },
    {
      provide: GENERATOR_ENV_TOKEN,
      useFactory: () => {
        return Object.assign(new GeneratorEnv(), {
          apiHost: inject(IS_BROWSER_TOKEN) ? environment.ipApiHost : environment.apiHost,
          editApiHost: environment.editApiHost,
          allowBackStageDomain: environment.allowBackStageDomain,
          recaptchaKey: environment.recaptchaKey,
          commonToken: environment.commonToken,
          accessTokenKey: 'accessToken',
          refreshTokenKey: 'refreshToken',
          googleSiteVerification: environment.googleSiteVerification,
        }) as GeneratorEnv;
      },
      multi: false,
    },
    { provide: APP_INITIALIZER, useFactory: initializationFactory, deps: [Injector], multi: true },
    {
      provide: CSP_NONCE,
      deps: [TransferState, [new Optional(), RESPONSE]],
      useFactory: (tState: TransferState, response: Response) => {
        if (inject(IS_BROWSER_TOKEN)) {
          return tState.get(CSP_NONCE_KEY, '');
        }
        return response.locals['cspNonce'];
      },
    },
    {
      provide: REQUEST_MARK_TOKEN,
      deps: [TransferState, [new Optional(), RESPONSE]],
      useFactory: (tState: TransferState, response: Response) => {
        if (inject(IS_BROWSER_TOKEN)) {
          return tState.get(REQUEST_MARK_KEY, '');
        }
        return response?.locals?.['requestMark'] ?? '';
      },
    },
    {
      provide: INTERNAL_KEY,
      deps: [TransferState, [new Optional(), RESPONSE]],
      useFactory: (tState: TransferState, response: Response) => {
        if (inject(IS_BROWSER_TOKEN)) {
          return tState.get(IS_INTERNAL_KEY, '');
        }
        return response?.locals?.['x-internal-key'] ?? '';
      },
    },
    { provide: UrlSerializer, useClass: HtmlSuffixURLSerializer },
    { provide: ErrorHandler, useClass: GlobalErrorHandlerService },
    provideHttpClient(withInterceptorsFromDi(), withFetch()),
    provideLuxonDateAdapter(DATE_FORMATS),
    provideAnimationsAsync(),
    provideClientHydration(withEventReplay(), withNoHttpTransferCache()),
    { provide: UPLOAD_ASSETS, useValue: { styles: ['tmUploadStyle.bundle.css'] } },
    provideRouter(routes, withEnabledBlockingInitialNavigation()),
  ],
};
